<template>
  <div>
    <b-overlay :show="isLoading">
      <b-card class="kyc">
        <h4 class="mb-2">{{ $t('kyc.kyc-verification') }}</h4>
        <b-row>
          <!-- CV -->
          <b-col cols="12">
            <b-button block variant="primary" class="text-left" v-b-toggle.cv-accordion
              @click="showingComponent = showingComponent === 'CV' ? '' : 'CV'"
            >
              <feather-icon v-if="showingComponent === 'CV'" icon="ChevronUpIcon" class="mr-1" />
              <feather-icon v-else icon="ChevronDownIcon" class="mr-1" />
              {{ $t('CV') }}
            </b-button>
            <p class="text-danger">
              {{ CvErrorMessage }}
            </p>
            <b-collapse
              id="cv-accordion"
              accordion="kyc"
              visible
            >
              <document-image
                ref="CvImage"
                :info-text="$t('kyc.cv-info')"
                :document-options="CVOptions"
                :document-images="cvImage"
                :pdf-allowed="true"
                :pdf-modal-title="$t('Your CV')"
                :full-width="true"
                :height="480"
                @documentRemoved="(id)=>{documentRemoved(id,'CV')}"
              />
            </b-collapse>
          </b-col>

          <b-col cols="12 pt-2">
            <b-button block variant="primary" class="text-left" v-b-toggle.id-accordion
              @click="showingComponent = showingComponent === 'ID' ? '' : 'ID'"
            >
              <feather-icon v-if="showingComponent === 'ID'" icon="ChevronUpIcon" class="mr-1" />
              <feather-icon v-else icon="ChevronDownIcon" class="mr-1" />
              {{ $t('kyc.id-documents') }}
            </b-button>
            <p class="text-danger">
              {{ errorMessage }}
            </p>
            <b-collapse id="id-accordion" accordion="kyc">
              <document-image
                ref="idCardImage"
                :document-options="idCardOptions"
                :document-images="documentImages"
                :info-text="$t('kyc.id-documents-info')"
                @documentRemoved="(id)=>{documentRemoved(id,'document')}"
              />
            </b-collapse>
          </b-col>

          <b-col cols="12 pt-2">
            <b-button
              v-b-toggle.address-accordion
              variant="primary"
              class="text-left" 
              block
              @click="showingComponent = showingComponent === 'ADDRESS' ? '' : 'ADDRESS'"
            >
              <feather-icon v-if="showingComponent === 'ADDRESS'" icon="ChevronUpIcon" class="mr-1" />
              <feather-icon v-else icon="ChevronDownIcon" class="mr-1" />
              {{ $t('kyc.proof-of-address') }}
            </b-button>
            <p class="text-danger">
              {{ proofOfAddressErrorMessage }}
            </p>
            <b-collapse id="address-accordion" accordion="kyc">
              <document-image
                ref="proofOfAddressImage"
                :info-text="$t('kyc.proof-of-address-info')"
                :document-options="proofOfAddressOptions"
                :document-images="proofOfAddressImages"
                @documentRemoved="(id)=>{documentRemoved(id,'proofOfAddress')}"
              />
            </b-collapse>
          </b-col>
        </b-row>

        <b-col
          class="text-right pt-2"
          cols="12"
        >

          <b-button
            variant="success"
            :disabled="!emptyDocuments"
            @click="saveKyc"
          >
            {{ $t('kyc.save') }}
          </b-button>
        </b-col>
      </b-card>
    </b-overlay>
  </div>
</template>
<script>
import useApollo from '@/plugins/graphql/useApollo'
import {
  BCard, BRow, BCol, BButton, BOverlay, BCollapse, VBToggle,
} from 'bootstrap-vue'
import { getUserData } from '@/auth/utils'
import DocumentImage from './DocumentImage.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BOverlay,
    BCollapse,
    DocumentImage,
  },
  directives: {
    'b-toggle': VBToggle,
  },
  data() {
    return {
      idCardOptions: {
        NATIONALID: this.$t('kyc.national-id'),
        PASSPORT: this.$t('kyc.passport'),
        DRIVINGLICENSE: this.$t('kyc.driving-license'),
        VOTERID: this.$t('kyc.voter-id'),
        VERIFIEDFORM: this.$t('kyc.verified-form'),
      },
      proofOfAddressOptions: {
        UTILITYBILL: this.$t('kyc.utility-bill'),
        PROPERTYTAXRECEIPT: this.$t('kyc.property-tax-receipt'),
        BANKSTATEMENT: this.$t('kyc.bank-statement'),
        NATIONALID: this.$t('kyc.national-id'),
        DRIVINGLICENSE: this.$t('kyc.driving-license'),
      },
      CVOptions: {
        CV: this.$t('CV'),
      },
      errorMessage: '',
      proofOfAddressErrorMessage: '',
      CvErrorMessage: '',
      form: {
        fullName: '',
      },
      userData: getUserData(),
      proofOfAddressImages: [],
      documentImages: [],
      cvImage: [],
      isMounted: false,
      isLoading: false,
      showingComponent: 'CV',
    }
  },

  computed: {
    emptyDocuments() {
      if (this.isMounted) {
        const proofDocuments = this.$refs?.proofOfAddressImage?.documentImages.filter(d => !d.verifiedAt)
        const idDocuments = this.$refs?.idCardImage?.documentImages.filter(d => !d.verifiedAt)
        const cvDocuments = this.$refs?.CvImage?.documentImages.filter(d => !d.verifiedAt)

        return idDocuments.length || proofDocuments.length || cvDocuments.length
      }
      return false
    },
  },

  mounted() {
    this.getUserDocuments()
    this.isMounted = true
  },

  methods: {
    getUserDocuments: _.debounce(function () {
      this.isLoading = true
      useApollo.users.getUserDocuments().then(res => {
        const data = res.data.me
        this.documentImages = data.documents.filter(d => d.documentType !== 'CV').map(({__typename, ...item}) => item)
        this.cvImage = data.documents.filter(d => d.documentType === 'CV').map(({__typename, ...item}) => item)
        this.proofOfAddressImages = data.proofOfAddress.map(({__typename, ...item}) => item)
      }).finally(() => {
        this.isLoading = false
      })
    }),

    documentRemoved(id, type) {
      const data = {
        userUid: this.userData.uuid,
        documentId: id,
        documentType: type,
      }
      useApollo.users.deleteUserDocuments(data).then(() => {
        this.showSuccess('Document Deleted Successfully')
        this.getUserDocuments()
      })
    },

    validateImages(documentImages, options) {
      let emptyDocuments = ''
      let errorMessage = ''
      documentImages.forEach(document => {
        if (!document.document) {
          let separator = ''
          if (emptyDocuments.length) {
            separator = ','
          }
          emptyDocuments += `${separator} ${options[document.documentType]}`
        }
      })

      if (emptyDocuments.length) {
        errorMessage = `Please upload image of your ${emptyDocuments.toLowerCase()}`
      }
      return errorMessage
    },

    saveKyc() {
      this.errorMessage = this.validateImages(this.$refs.idCardImage.documentImages, this.idCardOptions)
      this.proofOfAddressErrorMessage = this.validateImages(this.$refs.proofOfAddressImage.documentImages, this.proofOfAddressOptions)
      this.CvErrorMessage = this.validateImages(this.$refs.CvImage.documentImages, this.CVOptions)

      if (!this.errorMessage.length && !this.CvErrorMessage.length) {
        this.updateUserDocument([...this.$refs.idCardImage.documentImages, ...this.$refs.CvImage.documentImages])
      }
      if (!this.proofOfAddressErrorMessage) this.updateProofOfAddress(this.$refs.proofOfAddressImage.documentImages)
    },

    updateUserDocument(documentImages) {
      this.isLoading = true
      const filteredDocumentImages = documentImages.filter(d => !d.verifiedAt).map(({ verifiedAt, ...item }) => item)
      const data = {
        userUid: this.userData.uuid,
        input: filteredDocumentImages,
      }
      useApollo.users.updateUserDocument(data).then(res => {
        this.showSuccess(res.data.updateUserDocument.message)
        this.getUserDocuments()
      }).finally(() => {
        this.isLoading = false
      })
    },

    updateProofOfAddress(documentImages) {
      this.isLoading = true
      const filteredDocumentImages = documentImages.filter(d => !d.verifiedAt)
      const data = {
        userUid: this.userData.uuid,
        input: filteredDocumentImages,
      }
      useApollo.users.updateUserProofOfAddress(data).then(() => {
        this.getUserDocuments()
      }).finally(() => {
        this.isLoading = true
      })
    },
  },
}
</script>
